import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, Linkedin, Twitter, Facebook, Instagram, Youtube } from "lucide-react";
import { COMPANY, SUBSIDIARIES } from "@/lib/content";
import { BRAND } from "@/lib/assets";

export default function Footer() {
  const socials = [
    { name: "linkedin", icon: Linkedin, url: "" },
    { name: "twitter", icon: Twitter, url: "" },
    { name: "facebook", icon: Facebook, url: "" },
    { name: "instagram", icon: Instagram, url: "" },
    { name: "youtube", icon: Youtube, url: "" },
  ].filter((s) => s.url); // hide until admin adds

  return (
    <footer data-testid="site-footer" className="relative bg-navy-dark text-white overflow-hidden">
      {/* Watermark logo */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-end pr-[-6rem] opacity-[0.05]"
           aria-hidden="true">
        <img src={BRAND.logo}
             alt="" className="w-[900px] max-w-[130%] object-contain brightness-0 invert" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">
          <div>
            <img src={BRAND.logo}
                 alt="Salihi Group" className="h-12 w-auto brightness-0 invert mb-6"
                 data-testid="footer-logo" />
            <p className="text-white/70 text-sm leading-relaxed max-w-xs font-light">
              Salihi Group is a diversified professional services group established in 2020, headquartered in
              Mogadishu, Somalia. Our seven specialised companies bring legal, consulting, engineering, investment,
              mining, Oil & Gas, and workforce services together under one trusted corporate platform.
            </p>
            <div className="mt-6 flex items-center gap-2">
              <span className="gold-hairline" />
              <span className="text-gold text-[11px] uppercase tracking-[0.24em] font-medium">Est. {COMPANY.established}</span>
            </div>
          </div>

          <div>
            <h4 className="font-serif text-lg text-white mb-5" data-testid="footer-companies-heading">Group Companies</h4>
            <ul className="space-y-3">
              {SUBSIDIARIES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/companies/${s.slug}`} className="text-white/70 hover:text-gold text-sm transition-colors editorial-link"
                        data-testid={`footer-company-${s.slug}`}>
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg text-white mb-5">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-white/70 hover:text-gold text-sm transition-colors">About</Link></li>
              <li><Link to="/companies" className="text-white/70 hover:text-gold text-sm transition-colors">Our Companies</Link></li>
              <li><Link to="/sectors" className="text-white/70 hover:text-gold text-sm transition-colors">Sectors</Link></li>
              <li><Link to="/why-us" className="text-white/70 hover:text-gold text-sm transition-colors">Why Us</Link></li>
              <li><Link to="/governance" className="text-white/70 hover:text-gold text-sm transition-colors">Governance</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-gold text-sm transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg text-white mb-5">Contact</h4>
            <ul className="space-y-4 text-sm text-white/70">
              <li className="flex gap-3">
                <MapPin size={17} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                <span>{COMPANY.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone size={17} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="hover:text-gold" data-testid="footer-phone">{COMPANY.phone}</a>
              </li>
              <li className="flex gap-3">
                <Mail size={17} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-gold" data-testid="footer-email">{COMPANY.email}</a>
              </li>
              <li className="flex gap-3">
                <Clock size={17} strokeWidth={1.5} className="text-gold shrink-0 mt-0.5" />
                <span>{COMPANY.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        {socials.length > 0 && (
          <div className="mt-14 pt-8 border-t border-white/10 flex justify-start gap-3">
            {socials.map(({ name, icon: Icon, url }) => (
              <a key={name} href={url} target="_blank" rel="noreferrer"
                 className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
                 aria-label={name}>
                <Icon size={16} strokeWidth={1.5} />
              </a>
            ))}
          </div>
        )}

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/50">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Salihi Group. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/cookie-policy" className="hover:text-gold" data-testid="footer-cookie-policy">Cookie Policy</Link>
            <Link to="/privacy-policy" className="hover:text-gold" data-testid="footer-privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-gold" data-testid="footer-terms">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
